import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import { isThemeMode, setThemeMode, type ThemeMode } from '../lib/theme';
import { useResolvedTheme, useThemeMode } from './use-theme';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const OPTIONS: { mode: ThemeMode; label: string; icon: LucideIcon }[] = [
  { mode: 'system', label: 'System', icon: Monitor },
  { mode: 'light', label: 'Light', icon: Sun },
  { mode: 'dark', label: 'Dark', icon: Moon },
];

export default function ThemeToggle() {
  const mode = useThemeMode();
  const resolved = useResolvedTheme();
  // The trigger shows what is on screen, the menu shows which mode chose it, so
  // `System` still reads as light or dark rather than as a third icon.
  const TriggerIcon = resolved === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="icon">
          <TriggerIcon className="h-4 w-4" aria-hidden />
          <span className="sr-only">Theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        <DropdownMenuRadioGroup
          value={mode}
          onValueChange={(value) => {
            // Radix hands back a bare string; only a known mode may be applied.
            if (!isThemeMode(value)) return;
            setThemeMode(value);
          }}
        >
          {OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option.mode} value={option.mode}>
              <option.icon className="mr-2 h-4 w-4" aria-hidden />
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
